import { useState } from 'react';
import { api } from '../api.ts';
import { useApp } from '../store.tsx';
import { Field } from '../components.tsx';
import { ConnectPhoneBody } from './ConnectPhone.tsx';
import { parseAmountInput } from '../format.ts';

/**
 * Onboarding.
 *
 * Five short steps, of which only two are required. Income and savings goal are
 * explicitly skippable — pressuring someone for their salary before they trust
 * the app is how finance apps lose users at step one.
 */

const COUNTRIES = [
  { code: 'AE', name: 'United Arab Emirates', nameAr: 'الإمارات العربية المتحدة', currency: 'AED' },
  { code: 'SA', name: 'Saudi Arabia', nameAr: 'السعودية', currency: 'SAR' },
  { code: 'QA', name: 'Qatar', nameAr: 'قطر', currency: 'QAR' },
  { code: 'KW', name: 'Kuwait', nameAr: 'الكويت', currency: 'KWD' },
  { code: 'BH', name: 'Bahrain', nameAr: 'البحرين', currency: 'BHD' },
  { code: 'OM', name: 'Oman', nameAr: 'عُمان', currency: 'OMR' },
  { code: 'GB', name: 'United Kingdom', nameAr: 'المملكة المتحدة', currency: 'GBP' },
  { code: 'US', name: 'United States', nameAr: 'الولايات المتحدة', currency: 'USD' },
  { code: 'IN', name: 'India', nameAr: 'الهند', currency: 'INR' },
  { code: 'EG', name: 'Egypt', nameAr: 'مصر', currency: 'EGP' },
];

const CURRENCIES = ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'USD', 'EUR', 'GBP', 'INR', 'EGP'];

/**
 * The one setup question worth asking at signup.
 *
 * This step used to list five source types — SMS, email, CSV, manual entry, a
 * disabled bank connection — and let any number be ticked. That is the problem
 * seen from the app's side. From the user's there is one fact that decides
 * everything they have to do next, and it is which phone is in their hand: an
 * iPhone posts through a Shortcut because iOS lets no app read messages, an
 * Android device reads them directly. Everything else — a CSV, a typed-in
 * cash purchase — is available from inside the app the moment they are in, and
 * asking about it before they have seen a single transaction is four decisions
 * charged against a person who has not yet been shown anything.
 */
type Platform = 'ios' | 'android';

/** Preselect what they are almost certainly holding, still changeable. */
function detectPlatform(): Platform {
  return /android/i.test(navigator.userAgent) ? 'android' : 'ios';
}

export function OnboardingScreen({ onComplete }: { onComplete: () => void }): JSX.Element {
  const { t, locale } = useApp();
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState('AE');
  const [currency, setCurrency] = useState('AED');
  const [platform, setPlatform] = useState<Platform>(detectPlatform);
  const [income, setIncome] = useState('');
  // 0 is the sentinel for "another day", which reveals a free entry field —
  // distinct from null, which means no answer and leaves detection to it.
  const [payDay, setPayDay] = useState<number | null>(null);
  const [otherPayDay, setOtherPayDay] = useState<number | null>(null);
  const [goalName, setGoalName] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [goalDate, setGoalDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issuedKeys, setIssuedKeys] = useState<{ type: string; ingestKey: string }[]>([]);

  // iPhone first, and explained rather than named: it is the case where what
  // happens next is not what people expect, because iOS forbids the thing they
  // assume the app does.
  const PLATFORMS: { key: Platform; icon: string; title: string; desc: string }[] = [
    { key: 'ios', icon: '📱', title: t('platformIphone'), desc: t('platformIphoneDesc') },
    { key: 'android', icon: '🤖', title: t('platformAndroid'), desc: t('platformAndroidDesc') },
  ];

  /**
   * `overrides` exists because "Skip" cannot clear state and submit in the same
   * tick — `finish` would still read the pre-clear values from its closure and
   * create the very goal the user just declined.
   */
  const finish = async (overrides: { skipGoal?: boolean } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const incomeMinor = income ? parseAmountInput(income, currency) : null;
      const targetMinor = !overrides.skipGoal && goalAmount ? parseAmountInput(goalAmount, currency) : null;
      const goalTitle = overrides.skipGoal ? '' : goalName;

      const res = await api.post<{ sources: { type: string; ingestKey?: string }[] }>('/onboarding', {
        country,
        currency,
        locale,
        // Both phones deliver bank alerts as messages, so both are an `sms`
        // source. The platform is what decides the instructions.
        sourceTypes: ['sms'],
        platform,
        monthlyIncomeMinor: incomeMinor,
        payCycleDay: payDay === 0 ? otherPayDay : payDay,
        savingsGoal: goalTitle && targetMinor
          ? { name: goalTitle, targetAmountMinor: targetMinor, targetDate: goalDate || null }
          : null,
        aiConsent: false,
      });

      // Ingest keys are shown exactly once — the server stores only their hash.
      const keys = res.sources.filter((s): s is { type: string; ingestKey: string } => Boolean(s.ingestKey));
      if (keys.length) { setIssuedKeys(keys); setStep(5); }
      else onComplete();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const totalSteps = 5;

  return (
    <div className="screen" style={{ minHeight: '100dvh', maxWidth: 440, marginInline: 'auto', display: 'flex', flexDirection: 'column' }}>
      {step > 0 && step < 5 && (
        <div className="row row--between" style={{ marginBottom: 20 }}>
          <button className="btn btn--ghost btn--sm" onClick={() => setStep(step - 1)}>← {t('back')}</button>
          <span className="tiny muted">{t('step')} {step} {t('of')} {totalSteps - 1}</span>
        </div>
      )}

      {step === 0 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 20 }} aria-hidden>◈</div>
          <h1 style={{ fontSize: 28, fontWeight: 690, letterSpacing: '-0.03em', lineHeight: 1.22 }}>
            {t('welcomeTitle')}
          </h1>
          <p className="muted" style={{ marginTop: 14, fontSize: 15.5, lineHeight: 1.6 }}>{t('welcomeBody')}</p>
          <button className="btn btn--primary btn--block" style={{ marginTop: 32 }} onClick={() => setStep(1)}>
            {t('getStarted')}
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="stack stack--loose">
          <h2 style={{ fontSize: 21, fontWeight: 660 }}>{t('country')}</h2>
          <Field label={t('country')}>
            <select className="select" value={country} onChange={(e) => {
              setCountry(e.target.value);
              const c = COUNTRIES.find((x) => x.code === e.target.value);
              if (c) setCurrency(c.currency);
            }}>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{locale === 'ar' ? c.nameAr : c.name}</option>
              ))}
            </select>
          </Field>

          <Field label={t('currency')}>
            <select className="select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <button className="btn btn--primary btn--block" onClick={() => setStep(2)}>{t('next')}</button>
        </div>
      )}

      {step === 2 && (
        <div className="stack">
          <h2 style={{ fontSize: 21, fontWeight: 660 }}>{t('howTransactionsArrive')}</h2>
          <p className="small muted" style={{ marginBottom: 6 }}>{t('sourceHelp')}</p>

          {/* One choice, not a set of ticks: nobody has two phones in the hand
              they are signing up with, and the answer decides which set of
              instructions the rest of setup shows. */}
          <div role="radiogroup" aria-label={t('howTransactionsArrive')} className="stack stack--tight">
            {PLATFORMS.map((opt) => {
              const selected = platform === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setPlatform(opt.key)}
                  className="card card--interactive row"
                  style={{
                    gap: 14, textAlign: 'start', width: '100%', alignItems: 'flex-start',
                    borderColor: selected ? 'var(--accent)' : undefined,
                    background: selected ? 'var(--accent-soft)' : undefined,
                  }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1.2 }} aria-hidden>{opt.icon}</span>
                  <span className="grow">
                    <span style={{ display: 'block', fontWeight: 585, fontSize: 14.5 }}>{opt.title}</span>
                    <span className="tiny muted" style={{ display: 'block', marginTop: 3, lineHeight: 1.6 }}>
                      {opt.desc}
                    </span>
                  </span>
                  {selected && <span style={{ color: 'var(--accent)', fontWeight: 700 }} aria-hidden>✓</span>}
                </button>
              );
            })}
          </div>

          {/* The promise the whole product rests on, said before anything is
              asked for rather than buried in a privacy page. */}
          <p className="tiny muted" style={{ lineHeight: 1.6 }}>{t('sourceNoBankLogin')}</p>

          <button className="btn btn--primary btn--block" style={{ marginTop: 8 }} onClick={() => setStep(3)}>
            {t('next')}
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="stack stack--loose">
          <h2 style={{ fontSize: 21, fontWeight: 660 }}>{t('monthlyIncomeOptional')}</h2>
          <Field label={`${t('monthlyIncome')} (${currency})`} hint={t('incomeHelp')}>
            <input className="input" inputMode="decimal" value={income}
                   onChange={(e) => setIncome(e.target.value)} placeholder="—" />
          </Field>

          {/* Deliberately folded into this step rather than given its own.
              The original design avoided asking at all, on the grounds that a
              question at onboarding is one more thing between a new user and a
              working app — a good instinct that produced the wrong outcome,
              because without an answer the first cycle is a calendar month and
              a salary paid on the 27th funds the wrong month. Asking it beside
              income costs no extra screen: it is the same subject, and the
              answer is one tap. */}
          <Field
            label={locale === 'ar' ? 'متى يصلك راتبك؟' : 'When does your salary arrive?'}
            hint={locale === 'ar'
              ? 'يحدّد هذا بداية شهرك في التطبيق. يمكن تغييره لاحقًا.'
              : 'This decides where your month starts. You can change it later.'}
          >
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {[25, 26, 27, 28].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`chip${payDay === d ? ' chip--active' : ''}`}
                  onClick={() => setPayDay(payDay === d ? null : d)}
                >
                  {locale === 'ar' ? `يوم ${d}` : `${d}th`}
                </button>
              ))}
              <button
                type="button"
                className={`chip${payDay === 0 ? ' chip--active' : ''}`}
                onClick={() => setPayDay(payDay === 0 ? null : 0)}
              >
                {locale === 'ar' ? 'غير ذلك' : 'Another day'}
              </button>
            </div>
            {payDay === 0 && (
              <input
                className="input"
                style={{ marginTop: 8 }}
                inputMode="numeric"
                placeholder={locale === 'ar' ? 'يوم من الشهر (١–٣١)' : 'Day of the month (1–31)'}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setOtherPayDay(Number.isInteger(n) && n >= 1 && n <= 31 ? n : null);
                }}
              />
            )}
          </Field>

          <div className="row" style={{ gap: 10 }}>
            <button className="btn btn--secondary grow"
                    onClick={() => { setIncome(''); setPayDay(null); setStep(4); }}>{t('skip')}</button>
            <button className="btn btn--primary grow" onClick={() => setStep(4)}>{t('next')}</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="stack stack--loose">
          <h2 style={{ fontSize: 21, fontWeight: 660 }}>{t('savingsGoalOptional')}</h2>
          <Field label={t('goalName')}>
            <input className="input" value={goalName} onChange={(e) => setGoalName(e.target.value)}
                   placeholder={locale === 'ar' ? 'صندوق الطوارئ' : 'Emergency fund'} />
          </Field>
          <Field label={`${t('goalTarget')} (${currency})`}>
            <input className="input" inputMode="decimal" value={goalAmount}
                   onChange={(e) => setGoalAmount(e.target.value)} placeholder="—" />
          </Field>
          <Field label={t('goalDate')} error={error}>
            <input className="input" type="date" value={goalDate} onChange={(e) => setGoalDate(e.target.value)} />
          </Field>

          <div className="row" style={{ gap: 10 }}>
            <button className="btn btn--secondary grow" disabled={busy}
                    onClick={() => void finish({ skipGoal: true })}>
              {t('skip')}
            </button>
            {/* Not "Finish": connecting the phone comes next, and a button
                that claims the flow is over immediately followed by another
                screen reads as the app having lost its place. */}
            <button className="btn btn--primary grow" disabled={busy} onClick={() => void finish()}>
              {busy ? t('loading') : t('next')}
            </button>
          </div>
        </div>
      )}

      {/* ── Connect the phone ──────────────────────────────────────────────
          The account exists by now, so nothing here can be lost by leaving —
          and this is the screen that decides whether the app ever receives
          anything, so it is put in front of the user rather than left to be
          found in Settings. It is the same body Settings shows, so coming back
          to it later reads identically.

          The key signup just minted is shown here, inside its instructions,
          rather than alone on a screen of its own. */}
      {step === 5 && (
        <div className="stack stack--loose">
          <div>
            <h2 style={{ fontSize: 21, fontWeight: 660 }}>{t('connectPhoneTitle')}</h2>
            <p className="muted small" style={{ marginTop: 6, lineHeight: 1.6 }}>
              {t('connectPhoneBody')}
            </p>
          </div>

          <ConnectPhoneBody platform={platform} issuedKey={issuedKeys[0]?.ingestKey ?? null} />

          <button className="btn btn--primary btn--block" onClick={onComplete}>{t('done')}</button>
          {/* Findable again, so skipping now is not a dead end. */}
          <button className="btn btn--ghost btn--block btn--sm" onClick={onComplete}>
            {t('connectPhoneLater')}
          </button>
        </div>
      )}

    </div>
  );
}
